'use strict'

import Polyfills from './Polyfills.js'
import { Bartender } from '@fokke-/bartender.js'
import boringmenu from '@teppokoivula/boringmenu'
import Glightbox from 'glightbox'
import Swiper from 'swiper'
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/pagination'

/**
 * Sort items by a field with optional descending order
 *
 * @param {Array} items Array of elements with dataset attributes
 * @param {string} sortBy Field name, prefix with '-' for descending
 * @param {Function} getField Function to extract field value: (item, field) => value
 * @returns {Array} Sorted array (mutates original)
 */
const sortByField = (items, sortBy, getField) => {
	const isDesc = sortBy.startsWith('-')
	const field = isDesc ? sortBy.slice(1) : sortBy

	items.sort((a, b) => {
		const aVal = getField(a, field)
		const bVal = getField(b, field)

		let result
		if (typeof aVal === 'number') {
			result = aVal - bVal
		} else {
			result = String(aVal).localeCompare(String(bVal))
		}

		return isDesc ? -result : result
	})

	return items
}

/**
 * Site class contains general purpose site-specific features.
 *
 * @version 1.1.0
 */
export default class Site {
	/**
	 * Class constructor
	 *
	 * @param {Object} options Options for the class.
	 */
	constructor(options = {}) {
		this.options = {}

		// Init polyfills
		const polyfills = new Polyfills()
		polyfills.init()

		// Send X-Requested-With header for ProcessWire AJAX detection
		document.body.addEventListener('htmx:configRequest', (evt) => {
			evt.detail.headers['X-Requested-With'] = 'XMLHttpRequest'
		})

		// Strip empty params from HTMX GET requests
		document.body.addEventListener('htmx:configRequest', (evt) => {
			if (evt.detail.verb !== 'get') return
			for (const key of Object.keys(evt.detail.parameters)) {
				if (!evt.detail.parameters[key]) delete evt.detail.parameters[key]
			}
		})

		// Restore focus after HTMX swap for elements with data-focus-after-swap
		document.body.addEventListener('htmx:afterSwap', (evt) => {
			const selector = evt.detail.requestConfig?.elt?.dataset?.focusAfterSwap
			if (selector) {
				document.querySelector(selector)?.focus()
			}
		})

		// Init off-canvas after mobile menu is ready
		document.addEventListener('boringmenu-init-done', () => {
			if (window.bartender) return

			window.bartender = new Bartender()

			window.bartender.addBar('mobile-bar', {
				el: document.querySelector('.mobile-bar'),
				position: 'right',
			})

			document.querySelector('.js-toggle-mobile-bar').addEventListener('click', (event) => {
				window.bartender.toggle('mobile-bar', event.target)
			})

			document.querySelector('.js-close-mobile-bar').addEventListener('click', (event) => {
				window.bartender.close()
			})
		})

		// Init mobile menu
		window.addEventListener('DOMContentLoaded', () => {
			const mobileMenu = document.getElementById('mobile-menu')
			if (mobileMenu) {
				new boringmenu({
					selectors: {
						menu: '#mobile-menu > .sub-menu__list',
					},
					classes: {
						item: 'sub-menu__item',
						itemActive: 'sub-menu__item--current',
						itemParent: 'sub-menu__item--parent',
						toggle: 'sub-menu__toggle',
						toggleTextContainer: 'sr-only',
					},
					labels: {
						'menu.open': mobileMenu.getAttribute('data-labels-open') || 'open',
						'menu.close': mobileMenu.getAttribute('data-labels-close') || 'close',
					},
					icons: {
						'menu.open': 'icon-chevron-down',
						'menu.close': 'icon-chevron-up',
					},
				})
			}

			// Init sub menu (desktop sidebar only)
			const subMenu = document.querySelector('.sub-menu:not(.sub-menu--mobile):not([data-boringmenu-initialized])')
			if (subMenu) {
				subMenu.setAttribute('data-boringmenu-initialized', 'true')
				new boringmenu({
					selectors: {
						menu: '.sub-menu:not(.sub-menu--mobile) > .sub-menu__list--level-1',
					},
					classes: {
						item: 'sub-menu__item',
						itemActive: 'sub-menu__item--current',
						itemParent: 'sub-menu__item--parent',
						toggle: 'sub-menu__toggle',
						toggleTextContainer: 'sr-only',
					},
					labels: {
						'menu.open': subMenu.getAttribute('data-labels-open') || 'open',
						'menu.close': subMenu.getAttribute('data-labels-close') || 'close',
					},
					icons: {
						'menu.open': 'icon-chevron-down',
						'menu.close': 'icon-chevron-up',
					},
				})
			}
		})

		// Initialize
		this.init(options)
	}

	/**
	 * Init the class by calling applicable init methods
	 *
	 * @param {Object} options Options for the class.
	 * @return {Object}
	 */
	init(options = {}) {
		// Merge user options to the defaults
		this.options = {
			responsiveTables: {
				selector: 'main table',
			},
			imageLinks: {
				parentSelector: 'main',
			},
			...options,
		}

		// Call individual init methods
		this.initResponsiveTables()
		this.initSkipLinks()
		this.initImageLinks()
		this.initGalleries()
		this.initCopyLink()
		this.initHeroCarousel()
		this.initCarousels()
		this.initMaterialBankFilters()
		this.initFileSort()
		this.initSiteSearchFilters()
		this.initDiscussionsSearch()
		this.initAdminBarScrollFix()
		this.initDiscussionsFiles()
		this.initDiscussionsQuote()
		this.initDiscussionsLinks()
		this.initCartRemoveConfirm()
		this.initCartUpdate()
		this.initCartBadgeSync()
		this.initCopyEvents()
		// Dispatch custom event when init is done
		document.dispatchEvent(
			new CustomEvent('site-init-done', {
				bubbles: true,
				cancelable: true,
			})
		)

		return this
	}

	/**
	 * Cart update behaviour. Currently warns before navigating away if qty inputs
	 * have been changed without saving; may be replaced with auto-submit/AJAX later.
	 */
	initCartUpdate() {
		const form = document.querySelector('.cart-form')
		if (!form) return
		const qtyInputs = form.querySelectorAll('.cart-line__qty-input')
		if (!qtyInputs.length) return

		const isDirty = () =>
			Array.from(qtyInputs).some(
				(input) => input.value !== input.defaultValue
			)

		form.addEventListener('submit', () => {
			window.removeEventListener('beforeunload', handler)
		})

		const handler = (event) => {
			if (isDirty()) {
				event.preventDefault()
				event.returnValue = ''
			}
		}
		window.addEventListener('beforeunload', handler)
	}

	/**
	 * Confirm cart line removal before submitting the form.
	 */
	initCartRemoveConfirm() {
		document.querySelectorAll('.js-cart-remove').forEach((button) => {
			button.addEventListener('click', (event) => {
				const message = button.getAttribute('data-confirm')
				if (message && !window.confirm(message)) {
					event.preventDefault()
				}
			})
		})
	}

	/**
	 * Initialize responsive tables
	 *
	 * Finds content tables and wraps them with div.table-wrapper.
	 */
	initResponsiveTables() {
		document
			.querySelectorAll(this.options.responsiveTables.selector)
			.forEach((table) => {
				if (!table.closest('.table-wrapper')) {
					const tableWrapper = document.createElement('div')
					tableWrapper.classList.add('table-wrapper')
					tableWrapper.classList.add('overflow-x-auto')
					table.parentNode.insertBefore(tableWrapper, table)
					tableWrapper.appendChild(table)
				}
			})
	}

	/**
	 * Initialize skip links
	 *
	 * Finds skip links and enhances their behaviour for various screen readers and mobile devices.
	 */
	initSkipLinks() {
		const skipLinks = document.querySelectorAll(
			'.skip-link:not([data-skip-link])'
		)
		if (skipLinks.length) {
			const skipToBlur = (event) => {
				if (event.target.getAttribute('data-tabindex')) {
					event.target.removeAttribute('tabindex')
					event.target.removeAttribute('data-tabindex')
				}
			}
			skipLinks.forEach((skipLink) => {
				skipLink.setAttribute('data-skip-link', true)
				skipLink.addEventListener('click', (event) => {
					const skipTo = document.getElementById(
						event.target.href.split('#')[1]
					)
					if (skipTo && skipTo.getAttribute('tabindex') != '-1') {
						event.preventDefault()
						skipTo.setAttribute('tabindex', '-1')
						skipTo.setAttribute('data-tabindex', true)
						skipTo.addEventListener('blur', skipToBlur)
						skipTo.addEventListener('focusout', skipToBlur)
						skipTo.focus()
					}
				})
			})
		}
	}

	/**
	 * Initialize image links in content areas
	 *
	 * Auto-detects links to image files and opens them in lightbox.
	 */
	initImageLinks() {
		const parentNode = document.querySelector(this.options.imageLinks.parentSelector)
		if (!parentNode) return

		parentNode
			.querySelectorAll('a[href$=".jpg"], a[href$=".jpeg"], a[href$=".png"], a[href$=".gif"]')
			.forEach((link) => {
				if (!link.classList.contains('glightbox') && !link.hasAttribute('download')) {
					const figcaption = link.parentNode.querySelector('figcaption')
					if (figcaption && !link.getAttribute('data-title')) {
						link.setAttribute('data-title', figcaption.textContent)
					}
					link.classList.add('glightbox')
				}
			})

		if (document.querySelector('.glightbox')) {
			window.glightbox = Glightbox()
		}
	}

	/**
	 * Initialize thumbnail galleries
	 *
	 * Use .js-gallery container with .js-gallery-image and optional .js-gallery-thumb elements.
	 */
	initGalleries() {
		document.querySelectorAll('.js-gallery').forEach((gallery) => {
			const mainImage = gallery.querySelector('.js-gallery-image')
			const thumbs = gallery.querySelectorAll('.js-gallery-thumb')

			// Collect all image URLs for lightbox
			const images = []
			if (thumbs.length) {
				thumbs.forEach((thumb) => {
					images.push({ href: thumb.dataset.large, type: 'image' })
				})
			} else if (mainImage) {
				images.push({ href: mainImage.src, type: 'image' })
			}

			if (!images.length) return

			const lightbox = Glightbox({ elements: images })

			// Main image click opens lightbox
			if (mainImage) {
				mainImage.style.cursor = 'pointer'
				mainImage.addEventListener('click', () => {
					const currentSrc = mainImage.src
					const index = images.findIndex((img) => currentSrc.includes(img.href.split('/').pop()))
					lightbox.openAt(index >= 0 ? index : 0)
				})
			}

			// Thumbnail click swaps main image
			thumbs.forEach((thumb) => {
				thumb.addEventListener('click', () => {
					if (mainImage) {
						if (thumb.dataset.srcset) {
							mainImage.srcset = thumb.dataset.srcset
						}
						mainImage.src = thumb.dataset.large
						mainImage.alt = thumb.getAttribute('aria-label') || ''
					}
				})
			})
		})
	}

	/**
	 * Initialize copy link buttons
	 */
	initCopyLink() {
		document.querySelectorAll('[data-copy-url]').forEach((link) => {
			link.addEventListener('click', (event) => {
				event.preventDefault()
				const url = link.getAttribute('data-copy-url')
				navigator.clipboard.writeText(url).then(() => {
					link.classList.add('sharing-buttons__link--copied')
					setTimeout(() => {
						link.classList.remove('sharing-buttons__link--copied')
					}, 2000)
				})
			})
		})
	}

	/**
	 * Initialize hero carousel
	 */
	initHeroCarousel() {
		const carousel = document.querySelector('.js-hero-carousel')
		if (!carousel) return

		const dots = document.querySelector('.header__dots')

		new Swiper(carousel, {
			modules: [Autoplay, EffectFade, Pagination],
			effect: 'fade',
			fadeEffect: {
				crossFade: true,
			},
			speed: 500,
			autoplay: {
				delay: 5000,
				disableOnInteraction: false,
				pauseOnMouseEnter: true,
			},
			pagination: dots ? {
				el: dots,
				clickable: true,
			} : false,
		})
	}

	/**
	 * Initialize material bank filters
	 */
	initMaterialBankFilters() {
		// Runtime filter state (preserved across HTMX swaps)
		let filterState = { search: '', filetype: '', sort: 'title' }

		const filterCards = () => {
			const searchInput = document.querySelector('.js-material-search')
			const filetypeSelect = document.querySelector('.js-material-filetype')
			const query = searchInput?.value.toLowerCase() || ''
			const filetype = filetypeSelect?.value || ''

			document.querySelectorAll('.js-material-card').forEach((card) => {
				const matchesSearch = !query || card.dataset.search.includes(query)
				const matchesFiletype = !filetype || card.dataset.filetypes.includes(filetype)
				card.style.display = matchesSearch && matchesFiletype ? '' : 'none'
			})
		}

		const sortCards = () => {
			const sortSelect = document.querySelector('.js-material-sort')
			const grid = document.querySelector('.material-bank__grid')
			if (!sortSelect || !grid) return

			const sortBy = sortSelect.value
			const cards = Array.from(grid.querySelectorAll('.js-material-card'))

			sortByField(cards, sortBy, (item, field) => {
				const val = item.dataset[field] || ''
				return field === 'published' ? parseInt(val) : val
			})

			cards.forEach((card) => grid.appendChild(card))
		}

		const updatePageMeta = (title, category, baseUrl, baseTitle) => {
			// Update page title
			const h1 = document.querySelector('h1')
			if (h1) h1.textContent = title

			// Update document title
			const siteName = document.title.split('|').pop()?.trim() || ''
			document.title = siteName ? `${title} | ${siteName}` : title

			// Update breadcrumbs
			const breadcrumbsList = document.querySelector('.breadcrumbs__list')
			if (!breadcrumbsList) return

			const currentItem = breadcrumbsList.querySelector('.breadcrumbs__item--current')
			if (!currentItem) return

			if (category) {
				// Check if parent link already exists
				const parentLink = breadcrumbsList.querySelector(`a[href="${baseUrl}"]`)
				if (!parentLink) {
					// Add parent breadcrumb before current
					const parentItem = document.createElement('li')
					parentItem.className = 'breadcrumbs__item'
					parentItem.innerHTML = `<a class="breadcrumbs__link" href="${baseUrl}"><span>${baseTitle}</span></a><span class="breadcrumbs__separator" aria-hidden="true">/</span>`
					currentItem.parentNode.insertBefore(parentItem, currentItem)
				}
				// Update current breadcrumb
				currentItem.querySelector('span').textContent = title
				currentItem.querySelector('span').setAttribute('aria-label', `Current page: ${title}`)
			} else {
				// Remove category breadcrumb if exists (go back to base)
				const parentLink = breadcrumbsList.querySelector(`a[href="${baseUrl}"]`)
				if (parentLink) {
					parentLink.closest('.breadcrumbs__item').remove()
				}
				// Update current breadcrumb
				currentItem.querySelector('span').textContent = baseTitle
				currentItem.querySelector('span').setAttribute('aria-label', `Current page: ${baseTitle}`)
			}
		}

		// Category filter with HTMX
		document.addEventListener('change', (evt) => {
			if (evt.target.classList.contains('js-material-category')) {
				const select = evt.target
				const filters = select.closest('.js-material-bank-filters')
				const baseUrl = filters?.dataset.baseUrl || ''
				// Get base title from the "All" option
				const allOption = select.querySelector('option[value=""]')
				const baseTitle = allOption?.dataset.title || ''
				const selectedOption = select.options[select.selectedIndex]
				const title = selectedOption?.dataset.title || baseTitle
				const category = select.value

				const url = category ? `${baseUrl}${category}/` : baseUrl

				htmx.ajax('GET', url, {
					target: '#material-bank-content',
					select: '#material-bank-content',
					swap: 'outerHTML',
				})
				history.pushState({}, '', url)
				updatePageMeta(title, category, baseUrl, baseTitle)
			}
		})

		document.addEventListener('input', (evt) => {
			if (evt.target.classList.contains('js-material-search')) {
				filterCards()
			}
		})

		document.addEventListener('change', (evt) => {
			if (evt.target.classList.contains('js-material-filetype')) {
				filterCards()
			}
			if (evt.target.classList.contains('js-material-sort')) {
				sortCards()
			}
		})

		// Show loading state when HTMX request starts
		document.body.addEventListener('htmx:beforeRequest', (evt) => {
			if (evt.detail.target?.id !== 'material-bank-content') return
			const materialBank = document.querySelector('.material-bank')
			if (materialBank) materialBank.classList.add('is-loading')
		})

		// Save filter state before HTMX swap
		document.body.addEventListener('htmx:beforeSwap', (evt) => {
			if (evt.detail.target?.id !== 'material-bank-content') return
			const search = document.querySelector('.js-material-search')
			const filetype = document.querySelector('.js-material-filetype')
			const sort = document.querySelector('.js-material-sort')
			if (search) filterState.search = search.value
			if (filetype) filterState.filetype = filetype.value
			if (sort) filterState.sort = sort.value
		})

		// Restore filter state after HTMX swap
		document.body.addEventListener('htmx:afterSwap', (evt) => {
			if (evt.detail.target?.id !== 'material-bank-content') return
			const materialBank = document.querySelector('.material-bank')
			if (materialBank) materialBank.classList.remove('is-loading')
			const search = document.querySelector('.js-material-search')
			const filetype = document.querySelector('.js-material-filetype')
			const sort = document.querySelector('.js-material-sort')
			if (search) search.value = filterState.search
			if (filetype) filetype.value = filterState.filetype
			if (sort) sort.value = filterState.sort
			filterCards()
			sortCards()
		})

		// Collapse toggle
		document.addEventListener('click', (evt) => {
			const toggle = evt.target.closest('.js-collapse-toggle')
			if (!toggle) return

			const content = toggle.nextElementSibling
			if (!content) return

			const isCollapsed = content.classList.contains('is-collapsed')
			if (isCollapsed) {
				content.classList.remove('is-collapsed')
				toggle.setAttribute('aria-expanded', 'true')
			} else {
				content.classList.add('is-collapsed')
				toggle.setAttribute('aria-expanded', 'false')
			}
		})
	}

	/**
	 * Initialize file list sorting
	 */
	initFileSort() {
		document.addEventListener('change', (evt) => {
			if (!evt.target.classList.contains('js-file-sort')) return

			const sortBy = evt.target.value
			const list = document.querySelector('.js-file-list')
			if (!list) return

			const items = Array.from(list.querySelectorAll('.js-file-item'))

			sortByField(items, sortBy, (item, field) => {
				const val = item.dataset[field]
				return (field === 'size' || field === 'modified') ? parseInt(val) : val
			})

			items.forEach((item) => list.appendChild(item))
		})
	}

	/**
	 * Initialize site search filters
	 */
	initSiteSearchFilters() {
		const filtersContainer = document.querySelector('.js-site-search-filters')
		if (!filtersContainer) return

		const resultsContainer = document.getElementById('site-search-results')
		if (!resultsContainer) return

		const baseUrl = filtersContainer.dataset.searchUrl || ''

		// Get templates
		const resultTemplate = document.getElementById('search-result-template')
		const summaryTemplate = document.getElementById('search-summary-template')

		// Build URL with current filter values
		const buildUrl = () => {
			const params = new URLSearchParams()

			// Get search query
			const queryInput = document.querySelector('.js-search-query')
			if (queryInput?.value) {
				params.set('q', queryInput.value)
			}

			// Get all filter values
			document.querySelectorAll('.js-search-filter').forEach((input) => {
				const name = input.name
				const value = input.type === 'radio' ? (input.checked ? input.value : null) : input.value
				if (value) {
					params.set(name, value)
				}
			})

			return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl
		}

		// Find item by group name in items array
		const findItemByGroup = (items, group) => {
			if (!Array.isArray(items)) return null
			return items.find((item) => item.group === group) || null
		}

		// Render results from JSON data using templates
		const renderResults = (data) => {
			resultsContainer.innerHTML = ''

			// Get current template filter
			const templateFilter = filtersContainer.querySelector('input[name="t"]:checked')?.value || ''

			// Get results for the selected group, or all results
			let results = []
			let total = 0

			if (Array.isArray(data.items)) {
				if (templateFilter) {
					// Find item matching the selected group
					const item = findItemByGroup(data.items, templateFilter)
					if (item) {
						results = item.results || []
						total = item.total || 0
					}
				} else {
					// No filter - show first group's results
					if (data.items[0]) {
						results = data.items[0].results || []
						total = data.items[0].total || 0
					}
				}
			} else if (data.results) {
				// Non-grouped results
				results = data.results
				total = data.total || 0
			}

			// Create results wrapper
			const wrapper = document.createElement('div')
			wrapper.className = 'site-search__results-content'

			// Add summary
			if (summaryTemplate) {
				const summary = summaryTemplate.content.cloneNode(true)
				const countEl = summary.querySelector('.site-search__results-count')
				if (countEl) {
					const queryInput = document.querySelector('.js-search-query')
					const query = queryInput?.value || ''
					if (total === 0) {
						countEl.textContent = filtersContainer.dataset.noResults || 'No results found'
					} else if (total === 1) {
						countEl.textContent = (filtersContainer.dataset.oneResult || 'Found 1 result for "%s"').replace('%s', query)
					} else {
						countEl.textContent = (filtersContainer.dataset.manyResults || 'Found %d results for "%s"')
							.replace('%d', total)
							.replace('%s', query)
					}
				}
				wrapper.appendChild(summary)
			}

			// Add results list
			if (results.length > 0 && resultTemplate) {
				const list = document.createElement('ul')
				list.className = 'site-search__results-list'

				results.forEach((result) => {
					const item = resultTemplate.content.cloneNode(true)
					const dateEl = item.querySelector('.site-search__result-date')
					const link = item.querySelector('.site-search__result-link')
					const path = item.querySelector('.site-search__result-path')
					const excerpt = item.querySelector('.site-search__result-excerpt')

					// Show date for news and events
					if (dateEl && result.date && ['news-item', 'events-item'].includes(result.template)) {
						dateEl.textContent = result.date
					}
					if (link) {
						link.href = result.url || ''
						link.innerHTML = result.title || ''
					}
					if (path) path.textContent = result.url || ''
					if (excerpt && result.desc) excerpt.innerHTML = result.desc

					list.appendChild(item)
				})

				wrapper.appendChild(list)
			}

			resultsContainer.appendChild(wrapper)
		}

		// Update filter counts from JSON items (keeps existing labels)
		const updateFilterCounts = (items) => {
			const radioInputs = filtersContainer.querySelectorAll('input[name="t"]')
			if (!radioInputs.length || !Array.isArray(items)) return

			// First pass: calculate counts for each group and sum for "All"
			let allCount = 0
			const groupCounts = {}

			radioInputs.forEach((input) => {
				const group = input.value
				if (group !== '') {
					const item = findItemByGroup(items, group)
					const count = item?.total || 0
					groupCounts[group] = count
					allCount += count
				}
			})

			// Second pass: update labels
			radioInputs.forEach((input) => {
				const label = input.closest('label')?.querySelector('span')
				if (!label) return

				const group = input.value
				const count = group === '' ? allCount : (groupCounts[group] || 0)

				// Update count in label (remove old count, add new)
				const text = label.textContent.replace(/\s*\(\d+\)$/, '')
				label.textContent = `${text} (${count})`
			})
		}

		// Trigger search with current filters
		const triggerSearch = async () => {
			const queryInput = document.querySelector('.js-search-query')
			if (!queryInput?.value) return // Don't search without a query

			const url = buildUrl()
			const apiUrl = `${baseUrl}api/?${new URL(url, window.location.origin).searchParams.toString()}`

			// Show loading state
			const siteSearch = document.querySelector('.site-search')
			if (siteSearch) siteSearch.classList.add('is-loading')

			try {
				const response = await fetch(apiUrl, {
					headers: { 'X-Requested-With': 'XMLHttpRequest' },
				})
				const data = await response.json()

				// Render results using templates
				renderResults(data)

				// Update filter counts
				updateFilterCounts(data.items)

				// Update URL
				history.pushState({}, '', url)
			} catch (error) {
				console.error('Search error:', error)
			} finally {
				if (siteSearch) siteSearch.classList.remove('is-loading')
			}
		}

		// Update visibility of clear buttons based on their associated inputs
		const updateClearButtons = () => {
			filtersContainer.querySelectorAll('.js-clear-inputs').forEach((btn) => {
				const inputNames = (btn.dataset.clear || '').split(',').map((n) => n.trim())
				const hasValue = inputNames.some((name) => {
					const input = filtersContainer.querySelector(`[name="${name}"]`)
					return input?.value
				})
				btn.hidden = !hasValue
			})
		}

		// Initial visibility check
		updateClearButtons()

		// Listen for filter changes
		document.addEventListener('change', (evt) => {
			if (!evt.target.classList.contains('js-search-filter')) return
			updateClearButtons()
			triggerSearch()
		})

		// Clear inputs handler
		document.addEventListener('click', (evt) => {
			if (!evt.target.classList.contains('js-clear-inputs')) return

			const inputNames = (evt.target.dataset.clear || '').split(',').map((n) => n.trim())
			inputNames.forEach((name) => {
				const input = filtersContainer.querySelector(`[name="${name}"]`)
				if (input) input.value = ''
			})

			evt.target.hidden = true
			triggerSearch()
		})
	}

	/**
	 * Initialize card carousels with optional filtering
	 */
	initCarousels() {
		document.querySelectorAll('.js-carousel').forEach((carouselEl) => {
			const block = carouselEl.closest('[data-carousel-block]')
			const cardsContainer = block?.querySelector('[data-carousel-cards]')
			const filters = block?.querySelectorAll('[data-carousel-filter]') || []

			const container = carouselEl.closest('.carousel')
			const wrapper = carouselEl.querySelector('.swiper-wrapper')
			const dots = container?.querySelector('.carousel__dots')
			const prevBtn = container?.querySelector('.carousel__arrow--prev')
			const nextBtn = container?.querySelector('.carousel__arrow--next')

			const hasFiltering = cardsContainer && filters.length > 0
			const visibleLimit = parseInt(block?.getAttribute('data-visible-limit')) || 25
			const allCards = hasFiltering ? Array.from(cardsContainer.querySelectorAll('[data-carousel-card]')) : []

			let swiper = null
			let visibleCount = 1

			// Get cards for a category (only used when filtering is enabled)
			const getCardsForCategory = (category) => {
				if (category === 'all') {
					return allCards.slice(0, visibleLimit)
				} else if (category === 'latest') {
					return [...allCards]
						.sort((a, b) => parseInt(b.dataset.created) - parseInt(a.dataset.created))
						.slice(0, 12)
				} else if (category === 'popular') {
					return []
				} else {
					return allCards
						.filter((card) => card.getAttribute('data-category') === category)
						.slice(0, visibleLimit)
				}
			}

			// Hide filters that have no results
			if (hasFiltering) {
				filters.forEach((filter) => {
					const category = filter.getAttribute('data-carousel-filter')
					if (category === 'popular' || category === 'latest') {
						const cards = getCardsForCategory(category)
						if (cards.length === 0) {
							filter.hidden = true
						}
					}
				})
			}

			// Count how many slides fit in viewport
			const getVisibleCount = () => {
				const containerWidth = carouselEl.offsetWidth
				const firstSlide = carouselEl.querySelector('.swiper-slide')
				if (!firstSlide) return 1
				const slideWidth = firstSlide.offsetWidth + 24
				return Math.max(1, Math.round(containerWidth / slideWidth))
			}

			// Update pagination bullets
			const updatePagination = () => {
				if (!dots || !swiper) return
				visibleCount = getVisibleCount()
				const totalSlides = swiper.slides.length
				const pages = Math.ceil(totalSlides / visibleCount)

				let currentPage = Math.floor(swiper.activeIndex / visibleCount)
				if (swiper.activeIndex >= totalSlides - visibleCount) {
					currentPage = pages - 1
				}

				dots.innerHTML = ''
				for (let i = 0; i < pages; i++) {
					const bullet = document.createElement('span')
					bullet.className = 'swiper-pagination-bullet'
					if (i === currentPage) bullet.classList.add('swiper-pagination-bullet-active')
					bullet.addEventListener('click', () => swiper.slideTo(i * visibleCount))
					dots.appendChild(bullet)
				}
			}

			// Update arrow disabled states
			const updateArrows = () => {
				if (!swiper) return
				const totalSlides = swiper.slides.length
				if (prevBtn) prevBtn.classList.toggle('swiper-button-disabled', swiper.activeIndex === 0)
				if (nextBtn) nextBtn.classList.toggle('swiper-button-disabled', swiper.activeIndex >= totalSlides - visibleCount)
			}

			// Initialize swiper
			const initSwiper = () => {
				if (swiper) {
					swiper.destroy()
				}

				swiper = new Swiper(carouselEl, {
					modules: [Navigation, Pagination],
					slidesPerView: 'auto',
					spaceBetween: 24,
					speed: 400,
				})

				updatePagination()
				updateArrows()

				swiper.on('slideChange', () => {
					updatePagination()
					updateArrows()
				})
				swiper.on('resize', updatePagination)
			}

			// Populate swiper with cards matching category (only for filtered carousels)
			const populateSlides = (category) => {
				wrapper.innerHTML = ''

				const cardsToShow = getCardsForCategory(category)

				cardsToShow.forEach((card) => {
					const slide = document.createElement('div')
					slide.className = 'swiper-slide'
					slide.innerHTML = card.innerHTML
					wrapper.appendChild(slide)
				})

				initSwiper()
			}

			// Arrow navigation
			if (prevBtn) {
				prevBtn.addEventListener('click', (e) => {
					e.preventDefault()
					if (swiper) swiper.slideTo(Math.max(0, swiper.activeIndex - visibleCount))
				})
			}
			if (nextBtn) {
				nextBtn.addEventListener('click', (e) => {
					e.preventDefault()
					if (swiper) swiper.slideTo(swiper.activeIndex + visibleCount)
				})
			}

			// Filter click handlers
			if (hasFiltering) {
				filters.forEach((filter) => {
					filter.addEventListener('click', () => {
						const category = filter.getAttribute('data-carousel-filter')
						filters.forEach((f) => f.classList.remove('benefits-block__filter--active'))
						filter.classList.add('benefits-block__filter--active')
						populateSlides(category)
					})
				})

				// Initial population with filtering
				populateSlides('all')
			} else {
				// No filtering - just init swiper with existing slides
				initSwiper()
			}
		})
	}

	initDiscussionsSearch() {
		const toggle = document.getElementById('search-toggle')
		if (!toggle) return

		const panel = toggle.closest('.discussions')?.querySelector('.callout')
		if (!panel) return

		toggle.addEventListener('click', (e) => {
			e.preventDefault()
			panel.classList.toggle('is-open')
		})
	}

	initAdminBarScrollFix() {
		const adminBar = document.getElementById('adminbar')
		if (!adminBar) return

		const syncScrollPadding = () => {
			document.documentElement.style.scrollPaddingTop = adminBar.offsetHeight + 'px'
		}
		syncScrollPadding()
		new ResizeObserver(syncScrollPadding).observe(adminBar)

		if (window.location.hash) {
			window.addEventListener('load', () => {
				const target = document.getElementById(window.location.hash.slice(1))
				if (target) target.scrollIntoView()
			}, { once: true })
		}
	}

	initDiscussionsFiles() {
		const filesContainer = document.querySelector('.discussions-form .files')
		if (!filesContainer) return

		const MAX_FILES = 5
		const addFileLabel = filesContainer.closest('[data-add-file]')?.dataset.addFile ?? 'Add file'

		const addBtn = document.createElement('button')
		addBtn.type = 'button'
		addBtn.className = 'files__add'
		addBtn.textContent = addFileLabel
		filesContainer.appendChild(addBtn)

		const updateAddButton = () => {
			addBtn.hidden = filesContainer.querySelectorAll('.files__row').length >= MAX_FILES
		}

		// Keep only the first file span, remove extras (module HTML has two)
		Array.from(filesContainer.querySelectorAll('span'))
			.filter(span => span.querySelector('input[type="file"]'))
			.forEach((span, i) => {
				if (i === 0) span.className = 'files__row'
				else span.remove()
			})

		filesContainer.addEventListener('click', e => {
			if (e.target.classList.contains('files__add')) {
				e.preventDefault()
				if (filesContainer.querySelectorAll('.files__row').length >= MAX_FILES) return
				const span = document.createElement('span')
				span.className = 'files__row'
				const input = document.createElement('input')
				input.type = 'file'
				input.name = 'userfile[]'
				input.className = 'file'
				const remove = document.createElement('button')
				remove.type = 'button'
				remove.className = 'files__remove'
				remove.textContent = '- poista'
				span.appendChild(input)
				span.appendChild(remove)
				filesContainer.insertBefore(span, addBtn)
				updateAddButton()
			} else if (e.target.classList.contains('files__remove')) {
				e.preventDefault()
				e.target.closest('.files__row').remove()
				updateAddButton()
			}
		})
	}

	/**
	 * Sync cart badge from cart_qty cookie.
	 * Cookie is set by PHP after cart changes, readable by JS.
	 * Only updates if cookie exists; otherwise trusts server-rendered value.
	 */
	initCartBadgeSync() {
		const badge = document.querySelector('.js-cart-badge')
		if (!badge) return

		const match = document.cookie.match(/(?:^|;\s*)cart_qty=(\d+)/)
		if (!match) return

		const qty = parseInt(match[1], 10)
		if (qty > 0) {
			badge.textContent = qty
			badge.hidden = false
		} else {
			badge.textContent = ''
			badge.hidden = true
		}
	}

	initDiscussionsQuote() {
		const form = document.querySelector('.discussions-form')
		if (!form) return

		const clearQuote = () => {
			document.getElementById('discussions_quote')?.remove()
			form.querySelector('.qt')?.remove()
			document.querySelectorAll('a.quote').forEach(a => a.classList.remove('quote_active'))
		}

		document.addEventListener('click', e => {
			const btn = e.target.closest('a.quote')
			if (!btn) return

			if (btn.classList.contains('quote_active')) {
				clearQuote()
				return
			}

			clearQuote()
			btn.classList.add('quote_active')

			const qid = btn.dataset.id
			const input = document.createElement('input')
			input.type = 'hidden'
			input.id = 'discussions_quote'
			input.name = 'discussions_quote'
			input.value = qid
			form.appendChild(input)

			const msgEl = document.querySelector(`.discussions-message-${qid}`)
			const author = msgEl?.closest('.discussions-reply')
				?.previousElementSibling
				?.querySelector('.discussions-author')
				?.textContent?.trim() ?? ''
			const text = msgEl?.querySelector('p')?.innerHTML ?? ''

			const preview = document.createElement('div')
			preview.className = 'qt discussions-quote'
			preview.innerHTML = author
				? `<span class="discussions-quote-author">${author}</span>${text}`
				: text

			form.querySelector('label[for="discussions_message"]')?.after(preview)
		})
	}

	initDiscussionsLinks() {
		const containers = document.querySelectorAll('.discussions-message')
		if (!containers.length) return

		const URL_RE = /\bhttps?:\/\/[^\s<>"']+/g
		const TRAILING_PUNCT = /[.,;:!?)\]'"]+$/

		const isInsideAnchor = (node, root) => {
			let p = node.parentNode
			while (p && p !== root) {
				if (p.nodeName === 'A') return true
				p = p.parentNode
			}
			return false
		}

		const linkifyTextNode = (textNode) => {
			const text = textNode.nodeValue
			const frag = document.createDocumentFragment()
			let lastIdx = 0
			let match
			URL_RE.lastIndex = 0
			while ((match = URL_RE.exec(text))) {
				let url = match[0]
				const trail = url.match(TRAILING_PUNCT)
				const trailing = trail ? trail[0] : ''
				if (trailing) url = url.slice(0, -trailing.length)
				if (match.index > lastIdx) {
					frag.appendChild(document.createTextNode(text.slice(lastIdx, match.index)))
				}
				const a = document.createElement('a')
				a.href = url
				a.target = '_blank'
				a.rel = 'noopener noreferrer'
				a.textContent = url
				frag.appendChild(a)
				if (trailing) frag.appendChild(document.createTextNode(trailing))
				lastIdx = match.index + match[0].length
			}
			if (lastIdx === 0) return
			if (lastIdx < text.length) {
				frag.appendChild(document.createTextNode(text.slice(lastIdx)))
			}
			textNode.parentNode.replaceChild(frag, textNode)
		}

		containers.forEach((root) => {
			const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
			const nodes = []
			let n
			while ((n = walker.nextNode())) {
				if (!isInsideAnchor(n, root) && /\bhttps?:\/\//.test(n.nodeValue)) {
					nodes.push(n)
				}
			}
			nodes.forEach(linkifyTextNode)
		})
	}

	initCopyEvents() {
		document.addEventListener('click', (e) => {
			const btn = e.target.closest('.js-copy-events')
			if (!btn) return

			e.preventDefault()

			const list = document.querySelector('.events-calendar__list')
			if (!list) return

			const lines = []
			list.querySelectorAll('.events-calendar__list-item').forEach((item) => {
				const date = item.querySelector('.events-calendar__list-date')?.textContent?.trim() || ''
				const title = item.querySelector('.events-calendar__list-title')?.textContent?.trim() || ''
				if (date || title) {
					lines.push(`${date} ${title}`)
				}
			})

			const text = lines.join('\n')
			if (!text) return

			const showCopied = () => {
				btn.dataset.tooltip = btn.dataset.copiedText || 'Copied!'
				btn.classList.add('events-calendar__copy-button--copied')
				setTimeout(() => {
					btn.classList.remove('events-calendar__copy-button--copied')
					btn.dataset.tooltip = ''
				}, 2000)
			}

			if (navigator.clipboard && navigator.clipboard.writeText) {
				navigator.clipboard.writeText(text).then(showCopied)
			} else {
				const textarea = document.createElement('textarea')
				textarea.value = text
				textarea.style.position = 'fixed'
				textarea.style.opacity = '0'
				document.body.appendChild(textarea)
				textarea.select()
				document.execCommand('copy')
				document.body.removeChild(textarea)
				showCopied()
			}
		})
	}

}
